import { AreaChart, AreaChartProps } from '@mantine/charts'
import { Flex, Image, Text } from '@mantine/core'
import dayjs from 'dayjs'
import { useCallback, useMemo } from 'react'

import styles from '@/app/[locale]/price/[slug]/_components/CoinLineChart/components/CoinTitle/ShareModal/ShareDownloadHelper/index.module.css'
import { getLionInfo } from '@/app/[locale]/price/[slug]/_components/CoinLineChart/components/CoinTitle/ShareModal/ShareDownloadHelper/utils'
import {
  ShareModalContentBaseProps,
  ShareType,
} from '@/app/[locale]/price/[slug]/_components/CoinLineChart/components/CoinTitle/ShareModal/utils'
import { useSsrContext } from '@/contexts/ssr'
import { CoinPrices } from '@/types/coin'

import { getYAxisTicks } from '../../../../utils'
import ShareChartXAxisTick from './ShareChartXAxisTick'
import {
  baseShareAreaChartProps,
  baseShareAreaChartYAxisProps,
  DEFAULT_YAXIS_TICK_WIDTH,
  TICK_FONT_SIZE,
  XAIXIS_LINE_COLOR,
  XAIXIS_TICK_DISPLAY_COUNT,
} from './utils'

function useXAxisDateFormate(curTimeRange: string) {
  return useCallback(
    (timestamp: number) => {
      let template = 'D. MMM'
      if (['1H', '24H'].includes(curTimeRange)) {
        template = 'HH:mm'
      } else if (['1Y', 'ALL'].includes(curTimeRange)) {
        template = `MMM 'D`
      }
      return dayjs(timestamp).format(template)
    },
    [curTimeRange],
  )
}

function useShareAreaChartYTicks(data: { value: number; timestamp: number }[]) {
  return useMemo(() => {
    if (data.length === 0) return []
    const allValues = data.map(item => item.value)
    return getYAxisTicks(allValues)
  }, [data])
}

export function useShareAreaChartProps({
  currentCoinPrices,
  curTimeRange,
  tickFontSize: _tickFontSize,
}: {
  currentCoinPrices?: CoinPrices
  curTimeRange: string
  tickFontSize?: number
}) {
  const xAxisDateFormate = useXAxisDateFormate(curTimeRange)
  const data = useMemo(
    () =>
      currentCoinPrices?.prices.map(([timestamp, value]) => ({
        value,
        timestamp,
      })) || [],
    [currentCoinPrices],
  )
  const yTicks = useShareAreaChartYTicks(data)
  const maxYTickLength =
    yTicks.length > 0
      ? Math.max(...yTicks.map(tick => tick.toFixed(2).length))
      : DEFAULT_YAXIS_TICK_WIDTH
  const tickFontSize = _tickFontSize || TICK_FONT_SIZE
  return useMemo<AreaChartProps>(
    () => ({
      ...baseShareAreaChartProps,
      data,
      yAxisProps: {
        ...baseShareAreaChartYAxisProps,
        tick: {
          ...(baseShareAreaChartYAxisProps.tick as any),
          fontSize: tickFontSize,
        },
        width: maxYTickLength * tickFontSize * 0.7,
        ticks: yTicks,
      },
      xAxisProps: {
        height: tickFontSize * 4,
        tick: ({ x, y, payload: { index } }) => {
          const item = data[index]
          return (
            <ShareChartXAxisTick
              x={x}
              y={y}
              value={item.value}
              dateStr={xAxisDateFormate(item.timestamp * 1000)}
              tickFontSize={tickFontSize}
            />
          )
        },
        interval: Math.floor((data.length - 1) / XAIXIS_TICK_DISPLAY_COUNT),
        axisLine: { stroke: XAIXIS_LINE_COLOR },
        tickLine: { stroke: XAIXIS_LINE_COLOR },
      },
    }),
    [currentCoinPrices, xAxisDateFormate, yTicks, maxYTickLength, data, tickFontSize],
  )
}
export function useShareContent({
  currentCoinPrices,
  curTimeRange,
  shareType,
}: Omit<ShareModalContentBaseProps, 'socialType'>) {
  const shareAreaChartProps = useShareAreaChartProps({
    currentCoinPrices,
    curTimeRange,
    tickFontSize: 16,
  })
  const { lionDsc, lionImage } = getLionInfo(currentCoinPrices?.price_change ?? 0)
  const { selectedCurrency } = useSsrContext()
  const shareContent =
    shareType === ShareType.Chart ? (
      <Flex p={24} flex={1} direction={'column'} w={'100%'}>
        <Flex mb={16}>
          <Text flex={1}>Price (${selectedCurrency})</Text>
          <Text>{curTimeRange}</Text>
        </Flex>
        <AreaChart {...shareAreaChartProps} flex={1} w={'100%'} />
      </Flex>
    ) : (
      <Image src={lionImage} fit={'contain'} className={styles.lionImage} />
    )

  return {
    shareContent,
    lionDsc,
  }
}
